import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Chip,
    CircularProgress,
    Pagination,
    Stack,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PaymentsIcon from "@mui/icons-material/Payments";
import { API_ENDPOINTS, getData, postData } from "../../services/apiService.js";

/**
 * OPH-4 / OPH-A1 — Owner payout reconciliation report.
 *
 * Per-owner over a date range:
 *   earned     = sum of owner_subtotal across delivered packages on that
 *                owner's trucks
 *   paid       = sum of OwnerPays.payout_amount where transaction_type='Credit'
 *                on that owner's wallet — i.e. the platform's obligation
 *                discharged into the owner wallet.
 *   withdrawn  = OWO-001 — sum of completed bank-transfer-offline
 *                withdrawals (money owner pulled from wallet to bank).
 *                Informational; does not change `delta` because it comes
 *                out of already-credited wallet money.
 *   delta      = earned − paid (positive = owed, negative = overpaid)
 *
 * Visual cues:
 *   delta > 0 → amber chip ("Owed ₹X")
 *   delta < 0 → red chip   ("Overpaid ₹X")
 *   delta = 0 → green chip ("Settled")
 */
function Reconciliation() {
    const [from, setFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().slice(0, 10);
    });
    const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
    const [ownerId, setOwnerId] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Pay drill-down dialog — per-trip owed breakdown for a single owner.
    const [payDlg, setPayDlg] = useState({
        open: false,
        owner: null, // { owner_id, owner_name, delta } from the row
        loading: false,
        error: "",
        data: null, // server response: { trips: [...], wallet_id, total_owed, ... }
        submitting: null, // trip_id currently being credited
        notice: "", // success/info banner inside the dialog
    });

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(total / limit)),
        [total, limit]
    );

    const fmtINR = (n) =>
        new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 2,
        }).format(Number(n) || 0);

    const fmtDate = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return "—";
        }
    };

    const deltaChip = (delta) => {
        const v = Number(delta) || 0;
        if (Math.abs(v) < 0.01) {
            return <Chip label="Settled" size="small" sx={{ bgcolor: "#E6F4EA", color: "#188038", fontWeight: 600 }} />;
        }
        if (v > 0) {
            return (
                <Chip
                    label={`Owed ${fmtINR(v)}`}
                    size="small"
                    sx={{ bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 600 }}
                />
            );
        }
        return (
            <Chip
                label={`Overpaid ${fmtINR(-v)}`}
                size="small"
                sx={{ bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 600 }}
            />
        );
    };

    const fetchReport = useCallback(async () => {
        if (!from || !to) {
            setError("Pick both start and end dates.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const params = {
                from: new Date(from).toISOString(),
                to: new Date(to + "T23:59:59").toISOString(),
                page,
                limit,
            };
            if (ownerId) params.owner_id = ownerId;
            const res = await getData(API_ENDPOINTS.reconciliationReport, params);
            if (res?.statusCode === 200) {
                setRows(res.result?.rows ?? []);
                setTotal(res.result?.total ?? 0);
            } else {
                setError(res?.message || "Failed to load report.");
                setRows([]);
                setTotal(0);
            }
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.message ||
                "Failed to load report.";
            setError(msg);
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [from, to, ownerId, page, limit]);

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]); // refetch on page change; filters trigger via the Apply button

    const handleApply = () => {
        setPage(1);
        fetchReport();
    };

    // ── Pay drill-down ──────────────────────────────────────────────────────
    const fetchTripsOwed = useCallback(async (ownerId) => {
        setPayDlg((d) => ({ ...d, loading: true, error: "", data: null }));
        try {
            const url = API_ENDPOINTS.ownerTripsOwed.replace(":owner_id", ownerId);
            const res = await getData(url);
            if (res?.statusCode === 200) {
                setPayDlg((d) => ({ ...d, loading: false, data: res.result }));
            } else {
                setPayDlg((d) => ({
                    ...d,
                    loading: false,
                    error: res?.message || "Failed to load trips.",
                }));
            }
        } catch (e) {
            setPayDlg((d) => ({
                ...d,
                loading: false,
                error:
                    e?.response?.data?.message ||
                    e?.message ||
                    "Failed to load trips.",
            }));
        }
    }, []);

    const openPayDialog = (row) => {
        setPayDlg({
            open: true,
            owner: {
                owner_id: row.owner_id,
                owner_name: row.owner_name || `Owner #${row.owner_id}`,
                delta: Number(row.delta) || 0,
            },
            loading: true,
            error: "",
            data: null,
            submitting: null,
            notice: "",
        });
        fetchTripsOwed(row.owner_id);
    };

    const closePayDialog = () => {
        setPayDlg((d) => ({ ...d, open: false }));
    };

    const payTrip = async (trip) => {
        if (!payDlg.data?.wallet_id) {
            setPayDlg((d) => ({
                ...d,
                error: "Owner has no wallet — cannot initiate payout.",
            }));
            return;
        }
        if (trip.open_disputes > 0) {
            setPayDlg((d) => ({
                ...d,
                error: `Trip ${trip.trip_id} has ${trip.open_disputes} open dispute(s). Resolve them before paying.`,
            }));
            return;
        }
        setPayDlg((d) => ({
            ...d,
            submitting: trip.trip_id,
            error: "",
            notice: "",
        }));
        try {
            const res = await postData(API_ENDPOINTS.createOwnerPayment, {
                trip_id: trip.trip_id,
                wallet_id: payDlg.data.wallet_id,
                total_amount: trip.owner_subtotal_delivered,
                payout_amount: trip.owner_owed,
                payment_method: "wallet",
            });
            if (res?.statusCode === 200) {
                // Refetch the breakdown so the just-paid trip drops off / updates,
                // and refresh the parent reconciliation table so the row delta
                // reflects the new credit.
                setPayDlg((d) => ({
                    ...d,
                    submitting: null,
                    notice: `Trip ${trip.trip_id}: ₹${Number(trip.owner_owed).toFixed(2)} credited.`,
                }));
                await fetchTripsOwed(payDlg.owner.owner_id);
                fetchReport();
            } else {
                setPayDlg((d) => ({
                    ...d,
                    submitting: null,
                    error: res?.message || "Payout failed.",
                }));
            }
        } catch (e) {
            const status = e?.response?.status;
            const data = e?.response?.data;
            let msg = data?.message || e?.message || "Payout failed.";
            if (status === 409 && Array.isArray(data?.result?.blockers)) {
                msg = `${msg} Open: ${data.result.blockers
                    .map((b) => `${b.type === "complaint" ? "Complaint" : "Claim"} ${b.ref || `#${b.id}`} (${b.status})`)
                    .join(", ")}.`;
            }
            setPayDlg((d) => ({ ...d, submitting: null, error: msg }));
        }
    };

    const totals = useMemo(
        () => ({
            earned: rows.reduce((s, r) => s + Number(r.earned || 0), 0),
            paid: rows.reduce((s, r) => s + Number(r.paid || 0), 0),
            passThrough: rows.reduce((s, r) => s + Number(r.pass_through || 0), 0),
            withdrawn: rows.reduce((s, r) => s + Number(r.withdrawn || 0), 0),
            delta: rows.reduce((s, r) => s + Number(r.delta || 0), 0),
        }),
        [rows]
    );

    const T = { teal:"#007C84", tealDk:"#016268", tealLt:"#E0F4F5", amber:"#D97706", amberLt:"#FEF3C7", emerald:"#059669", emeraldLt:"#D1FAE5", rose:"#E11D48", roseLt:"#FFE4E6", indigo:"#4F46E5", indigoLt:"#EEF2FF", bg:"#F7FAFC", border:"#E2E8F0", text:"#0F172A", muted:"#64748B" };

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>
            {/* Hero header */}
            <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3, boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
                <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Owner Payout Reconciliation</Typography>
                <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.75)", mt:0.5 }}>
                    Earned vs paid vs withdrawn — delta shows what each owner is still owed
                </Typography>
            </Box>

            {/* Filter card */}
            <Card sx={{ mb:3, borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)" }}>
                <CardContent>
                    <Stack direction={{ xs:"column", sm:"row" }} spacing={2} alignItems="end">
                        <TextField label="From" type="date" size="small" value={from} onChange={e=>setFrom(e.target.value)} InputLabelProps={{ shrink:true }} sx={{ minWidth:160, "& .MuiOutlinedInput-root":{ borderRadius:"12px" } }} />
                        <TextField label="To"   type="date" size="small" value={to}   onChange={e=>setTo(e.target.value)}   InputLabelProps={{ shrink:true }} sx={{ minWidth:160, "& .MuiOutlinedInput-root":{ borderRadius:"12px" } }} />
                        <TextField label="Owner ID (optional)" type="number" size="small" value={ownerId} onChange={e=>setOwnerId(e.target.value)} sx={{ minWidth:200, "& .MuiOutlinedInput-root":{ borderRadius:"12px" } }} />
                        <Button variant="contained" disableElevation onClick={handleApply} disabled={loading}
                            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:T.teal, "&:hover":{ bgcolor:T.tealDk }, height:40 }}>
                            Apply
                        </Button>
                    </Stack>
                    {error && <Alert severity="error" sx={{ mt:2, borderRadius:"10px" }}>{error}</Alert>}
                </CardContent>
            </Card>

            <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)" }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {total} owner{total === 1 ? "" : "s"} in range
                        </Typography>
                        <Stack direction="row" spacing={3}>
                            <Tooltip title="Sum of `earned` for currently visible page">
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Page earned
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {fmtINR(totals.earned)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip title="Sum of owner-net `paid` (credited to owner wallet, excluding driver-incentive pass-through) for currently visible page">
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Page paid
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {fmtINR(totals.paid)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip title="Sum of driver-incentive pass-through bundled into payouts. Owner is forwarding this to their drivers; not owner earnings.">
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Page pass-through
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight={600}
                                        color={totals.passThrough > 0 ? "#7C3AED" : "text.primary"}
                                    >
                                        {fmtINR(totals.passThrough)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip title="Sum of `withdrawn` (offline bank transfers completed) for currently visible page">
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Page withdrawn
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600} color="#0F766E">
                                        {fmtINR(totals.withdrawn)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip title="Sum of `delta` for currently visible page">
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Page delta
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {fmtINR(totals.delta)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Stack>
                    </Box>

                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:"12px" }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: T.bg }}>
                                <TableRow sx={{ "& th":{ fontWeight:700, fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px", py:1.5, borderBottom:`2px solid ${T.border}` } }}>
                                    <TableCell>Owner</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Sum of owner_subtotal for delivered packages in range">
                                            <span>Earned</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Owner-net portion of OwnerPays Credit rows in range (payout_amount minus driver-incentive pass-through). Compare against Earned.">
                                            <span>Paid</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Driver-incentive amount bundled into payouts that the owner is forwarding to their drivers. Not owner earnings; broken out so Paid stays comparable to Earned.">
                                            <span>Pass-through</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Bank-transferred to owner via completed withdrawals in range. Informational — does not affect delta.">
                                            <span>Withdrawn</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">Delta</TableCell>
                                    <TableCell align="right">Status</TableCell>
                                    <TableCell>Last activity</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loading && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            <Typography color="text.secondary" py={2}>
                                                No owners with activity in this date range.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {rows.map((r) => {
                                    const lastActivity =
                                        [r.last_payout_at, r.last_withdrawal_at]
                                            .filter(Boolean)
                                            .sort()
                                            .pop() || null;
                                    return (
                                        <TableRow key={r.owner_id} hover>
                                            <TableCell>
                                                <Typography fontWeight={600}>
                                                    {r.owner_name || `Owner #${r.owner_id}`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ID {r.owner_id}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">{fmtINR(r.earned)}</TableCell>
                                            <TableCell align="right">{fmtINR(r.paid)}</TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: Number(r.pass_through) > 0 ? "#7C3AED" : "text.disabled",
                                                        fontWeight: Number(r.pass_through) > 0 ? 600 : 400,
                                                    }}
                                                >
                                                    {fmtINR(r.pass_through || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: Number(r.withdrawn) > 0 ? "#0F766E" : "text.primary",
                                                            fontWeight: Number(r.withdrawn) > 0 ? 600 : 400,
                                                        }}
                                                    >
                                                        {fmtINR(r.withdrawn)}
                                                    </Typography>
                                                    {Number(r.withdrawn_count) > 0 && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {r.withdrawn_count} txn{r.withdrawn_count === 1 ? "" : "s"}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {fmtINR(r.delta)}
                                            </TableCell>
                                            <TableCell align="right">{deltaChip(r.delta)}</TableCell>
                                            <TableCell>{fmtDate(lastActivity)}</TableCell>
                                            <TableCell align="right">
                                                {Number(r.delta) > 0.01 && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<PaymentsIcon fontSize="small" />}
                                                        onClick={() => openPayDialog(r)}
                                                    >
                                                        Pay
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination
                                page={page}
                                count={totalPages}
                                onChange={(_, p) => setPage(p)}
                                color="primary"
                                size="small"
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Pay drill-down dialog — per-trip outstanding for one owner. */}
            <Dialog
                open={payDlg.open}
                onClose={closePayDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: "bold", pb: 1 }}>
                    Initiate payout —{" "}
                    {payDlg.owner ? payDlg.owner.owner_name : ""}
                    {payDlg.owner && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (ID {payDlg.owner.owner_id})
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {payDlg.loading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : payDlg.error && !payDlg.data ? (
                        <Alert severity="error">{payDlg.error}</Alert>
                    ) : payDlg.data && payDlg.data.trips.length === 0 ? (
                        <Alert severity="info">
                            Nothing currently owed at the trip level for this owner.
                            The reconciliation delta of {fmtINR(payDlg.owner?.delta || 0)} reflects activity in
                            the selected date range; the underlying trips may already
                            be fully credited (or over-credited) — see Transactions for
                            an audit trail.
                        </Alert>
                    ) : (
                        payDlg.data && (
                            <>
                                <Stack direction="row" spacing={3} mb={2} flexWrap="wrap">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Total trip-level owed
                                        </Typography>
                                        <Typography variant="h6" color="primary">
                                            {fmtINR(payDlg.data.total_owed)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Wallet balance
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600}>
                                            {fmtINR(payDlg.data.wallet_balance)}
                                        </Typography>
                                    </Box>
                                </Stack>
                                {payDlg.notice && (
                                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPayDlg((d) => ({ ...d, notice: "" }))}>
                                        {payDlg.notice}
                                    </Alert>
                                )}
                                {payDlg.error && (
                                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPayDlg((d) => ({ ...d, error: "" }))}>
                                        {payDlg.error}
                                    </Alert>
                                )}
                                <Divider />
                                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: "#F8F9FA" }}>
                                            <TableRow>
                                                <TableCell>Trip</TableCell>
                                                <TableCell>Route</TableCell>
                                                <TableCell align="right">Delivered</TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Sum of pricing_meta.owner_subtotal across delivered packages on this trip">
                                                        <span>Earned</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right">Already paid</TableCell>
                                                <TableCell align="right">Owed</TableCell>
                                                <TableCell align="right">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {payDlg.data.trips.map((t) => (
                                                <TableRow key={t.trip_id} hover>
                                                    <TableCell>
                                                        <Typography fontWeight={600}>#{t.trip_id}</Typography>
                                                        {t.actual_delivery_at && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {fmtDate(t.actual_delivery_at)}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {t.from_city && t.to_city
                                                            ? `${t.from_city} → ${t.to_city}`
                                                            : "—"}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {t.delivered_packages}
                                                        {t.active_packages > t.delivered_packages && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                                / {t.active_packages}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">{fmtINR(t.owner_subtotal_delivered)}</TableCell>
                                                    <TableCell align="right">{fmtINR(t.owner_already_paid)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                                                        {fmtINR(t.owner_owed)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {t.open_disputes > 0 ? (
                                                            <Tooltip title={`${t.open_disputes} open dispute(s) — resolve before paying.`}>
                                                                <span>
                                                                    <Button size="small" variant="outlined" color="warning" disabled>
                                                                        Blocked
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="primary"
                                                                disabled={payDlg.submitting != null}
                                                                onClick={() => payTrip(t)}
                                                                startIcon={
                                                                    payDlg.submitting === t.trip_id ? (
                                                                        <CircularProgress size={14} color="inherit" />
                                                                    ) : (
                                                                        <PaymentsIcon fontSize="small" />
                                                                    )
                                                                }
                                                            >
                                                                {payDlg.submitting === t.trip_id ? "Paying…" : `Pay ${fmtINR(t.owner_owed)}`}
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePayDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Reconciliation;
